/*
 * Written by Alexander Agudelo < alex@kemu.io >, 2021
 * Date: 29/Jul/2021
 * Last Modified: 09/09/2025, 9:27:44 am
 * Modified By: Alexander Agudelo
 * Description:  Stores widgets contents locally
 * 
 * ------
 * Copyright (C) 2021 Kemu - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential.
 */

import { safeJsonParse } from '@common/utils';
import { u8Arr2Str, ab2Str } from '@kemu-io/kemu-core/common/utils';
let cache: Cache;

const WIDGET_CACHE_PROTOCOL_VERSION = '1.2';
/**
 * Initializes the widget cache
 */
const initialize = async (): Promise<void> => {
	if (!('storage' in navigator)) {
		return console.error('Storage API not supported by this browser');
	}

	cache = await caches.open('kemu-widgets-cache');
};

const isInitialized = (): boolean => !!cache;

const getCacheLocation = (id: string/* , version: number */): string => {
	// return `/widgets/${id}/${version}`;
	return `/widgets/${id}`;
};

/**
 * Adds the widget contents to the local cache.
 */
const storeWidget = async (id: string, version: number, contents: string | Uint8Array): Promise<void> => {
	const headers = new Headers({
		'version': String(version),
		'createdAt': new Date().toJSON(),
		'content-type': 'application/json;charset=UTF-8',
		'content-length': String(contents.length),
		// Added on 23/04/2025 to signal bug fix caused by invalid cached content
		'protocol-version': WIDGET_CACHE_PROTOCOL_VERSION
	});

	const url = getCacheLocation(id);
	await cache.put(url, new Response(contents as string, { headers }));
};

/**
 * Removes the given widget from the local cache.
 */
const deleteWidget = async (id: string/* , version: number */): Promise<void> => {
	try {
		await cache.delete(getCacheLocation(id));
	} catch (e) {
		console.error(`Error deleting widget [${id}]: `, e);
	}
};

const getStringContents = (data: ArrayBuffer): string => {
	// NOTE: widgets contents used to be encoded using 16 bit encoding, and since upload was directly to s3, 
	// it used to be stored that way. Now however, the contents are uploaded to the BE, which uses uint8 encoding.
	// Therefore, we need to convert the contents to a string using uint8 encoding.
	let contents = u8Arr2Str(new Uint8Array(data));
	const parsed = safeJsonParse(contents);
	// If uint8 decoding failed, try uint16 decoding
	if (!parsed) {
		contents = ab2Str(data);
	}

	return contents;
};
/**
 * Gets the widgets contents from cache or it downloads it from the given url
 * if missing.
 */
const getWidget = async <T extends boolean = false>(
	id: string,
	version: number,
	downloadUrl: string,
	asArrayBuffer?: T
): Promise<(T extends true ? Uint8Array : string) | null> => {
	try {
		const response = await cache.match(getCacheLocation(id/* , version */));
		const cachedProtocolVersion = response?.headers.get('protocol-version');
		const isOlderProtocolVersion = cachedProtocolVersion !== WIDGET_CACHE_PROTOCOL_VERSION;
	
		if (!response || isOlderProtocolVersion) {
			// Download instead
			const fetchResponse = await fetch(downloadUrl);
			if (!fetchResponse.ok) {
				console.log(`Error downloading widget [${id}]: ${fetchResponse.statusText}`);
				return null;
			}
			const data = await fetchResponse.arrayBuffer();
			if (asArrayBuffer) {
				return new Uint8Array(data) as T extends true ? Uint8Array : string;
			} else {
				const contentsString = getStringContents(data);
				if (!contentsString) {
					console.error(`Error getting widget [${id}]: failed to get string contents`);
					return null;
				}
				await storeWidget(id, version, contentsString);
				return contentsString as T extends true ? Uint8Array : string;
			}
		} else {
			const data = await response.arrayBuffer();
			const binary = new Uint8Array(data);
			if (asArrayBuffer) {
				return binary as T extends true ? Uint8Array : string;
			} else {
				return u8Arr2Str(binary) as T extends true ? Uint8Array : string;
			}
		}
	} catch (e) {
		console.error(`Error getting widget [${id}]: `, e);
		return null;
	}
};

export default {
	initialize,
	storeWidget,
	getWidget,
	isInitialized,
	deleteWidget
};
